/**
 * IntegratedHome.jsx — 통합 관리 홈 /admin/integrated (2026-10-01, 사용자: "통합메뉴들을 거기서 모아보고 관리하고 싶거든")
 *
 * 작업 대상 회사 칸의 "통합관리"를 고르면 오는 곳. 머리말 → 요약 칸 → [메뉴 목록 | 기능 × 회사 표] (휴대폰은 위아래).
 *
 * 데이터 (서버 주소는 모두 기존 것)
 *   운영자 : /api/admin/integrated-menus (배정 수 포함) — 전 테넌트 메뉴라서 **고른 테넌트 회사 안의 메뉴만** 쓴다.
 *            편집 창의 회사 목록도 고른 테넌트 회사만 — 다른 테넌트 회사와 묶는 메뉴를 만들 수 없게.
 *   그 외  : 작업 대상이 가진 내 열쇠 목록(/api/my/integrated-menus). 관리 주소는 403 이라 부르지 않는다.
 *            편집·배정·새 메뉴 단추와 배정 수가 없다.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../../components/Toast';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useCompanyScope } from '../adminlayout/CompanyScopeContext';
import { scopedIntegratedMenus } from '../adminlayout/integratedScope';
import { stdPageTitle, stdPageSubtitle, stdOutlineBtn, STD_HEADER_TOP } from '../adminlayout/designStandard';
import { useIntegratedMenu } from '../integratedmenu/useIntegratedMenu';
import { IntegratedMenuFormModal } from '../integratedmenu/IntegratedMenuFormModal';
import { homeMenuRows, homeSummary, homeMatrix } from './integratedHomeModel';
import IntegratedHomeTiles from './IntegratedHomeTiles';
import IntegratedHomeMenuList from './IntegratedHomeMenuList';
import IntegratedHomeMatrix from './IntegratedHomeMatrix';

export default function IntegratedHome() {
  const scope = useCompanyScope();
  const isOperator = Boolean(scope?.isOperator);
  // 고칠 수 있는 사람 = 운영자 또는 '통합 메뉴 관리' 열쇠(2026-10-15, 테넌트 관리자 자동) — 관리 주소는 자기 테넌트 메뉴만 준다
  const canManage = isOperator || Boolean(scope?.canManageIntegrated);
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const hook = useIntegratedMenu({ showToast, enabled: canManage });
  // 편집 창: null = 닫힘 / { menu: null } = 새 메뉴 / { menu, initialCodes? } = 편집(표의 "+" 는 그 회사를 미리 켬)
  const [modal, setModal] = useState(null);

  // 회사 단추: 운영자 = 작업 대상 회사 / 열쇠 보유자 = 서버가 준 자기 테넌트 회사(useIntegratedMenu)
  const tenantCompanies = isOperator
    ? (scope?.companies || []).map((c) => ({ code: c.code, name: c.name || c.code }))
    : hook.companies;
  const menus = canManage
    ? scopedIntegratedMenus({ isOperator: true, list: hook.list, tenantCompanyCodes: tenantCompanies.map((c) => c.code) })
    : (scope?.integratedMenus || []);
  const hqCode = scope?.hqCompanyCode;

  if (canManage && !hook.loaded) return <div style={{ padding: 24 }}>로딩 중...</div>;

  async function handleSubmit(payload) {
    const r = modal?.menu ? await hook.update(modal.menu.id, payload) : await hook.create(payload);
    if (r.ok) setModal(null);
  }
  async function handleDelete(menu) {
    if (await hook.remove(menu.id, menu.assignees_count)) setModal(null);
  }

  return (
    <div className="flex max-w-[1400px] flex-col gap-5">
      <div
        className={isMobile ? 'flex flex-col gap-3 pt-1' : 'flex items-start justify-between gap-4'}
        style={isMobile ? undefined : { marginTop: STD_HEADER_TOP }}
      >
        <div>
          <h2 style={{ ...stdPageTitle, fontSize: isMobile ? 24 : 26 }}>통합 관리</h2>
          <div style={stdPageSubtitle}>여러 회사를 한 화면에서 보는 메뉴를 모아 보고 관리합니다.</div>
        </div>
        {/* 업무/기능 배정 바로가기는 운영자만 — 열쇠 보유자 중엔 그 화면 열쇠가 없는 사람도 있다(테넌트 관리자는 사이드바에 있다).
            '+ 새 통합 메뉴'는 '통합 메뉴' 카드 제목 줄 오른쪽 끝으로 옮겼다(2026-10-15 사용자). */}
        {isOperator && (
          <div className="flex flex-shrink-0 gap-2">
            <Link to="/admin/work-feature-assignments" style={{ ...stdOutlineBtn, textDecoration: 'none' }}>업무/기능 배정</Link>
          </div>
        )}
      </div>

      <IntegratedHomeTiles summary={homeSummary(menus, hqCode)} isOperator={canManage} isMobile={isMobile} />

      <div
        className="grid items-start gap-4"
        style={{ gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1.65fr) minmax(0, 1fr)' }}
      >
        <IntegratedHomeMenuList
          rows={homeMenuRows(menus)}
          hqCode={hqCode}
          isOperator={canManage}
          isMobile={isMobile}
          onEdit={(menu) => setModal({ menu })}
          onCreate={canManage ? () => setModal({ menu: null }) : null}
        />
        <IntegratedHomeMatrix
          matrix={homeMatrix(menus, hqCode)}
          isOperator={canManage}
          onAddCompany={(menu, code) => setModal({ menu, initialCodes: [...(menu.company_codes || []), code] })}
        />
      </div>

      {canManage && (
        <IntegratedMenuFormModal
          open={Boolean(modal)}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          editing={modal?.menu || null}
          initialCodes={modal?.initialCodes || null}
          companies={tenantCompanies}
          allowedFeatures={hook.allowedFeatures}
          menus={menus}
        />
      )}
    </div>
  );
}
